import type { AttributeT, SelectInputT, SimpleStyleT, TextI } from "tweenly"
import FormatAlignCenterRoundedIcon from "@mui/icons-material/FormatAlignCenterRounded"
import FormatAlignLeftRoundedIcon from "@mui/icons-material/FormatAlignLeftRounded"
import FormatAlignRightRoundedIcon from "@mui/icons-material/FormatAlignRightRounded"
import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded"
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded"
import FormatShapesRoundedIcon from "@mui/icons-material/FormatShapesRounded"
import FormatTextdirectionLToRRoundedIcon from "@mui/icons-material/FormatTextdirectionLToRRounded"
import FormatTextdirectionRToLRoundedIcon from "@mui/icons-material/FormatTextdirectionRToLRounded"
import VerticalAlignBottomRoundedIcon from "@mui/icons-material/VerticalAlignBottomRounded"
import VerticalAlignCenterRoundedIcon from "@mui/icons-material/VerticalAlignCenterRounded"
import VerticalAlignTopRoundedIcon from "@mui/icons-material/VerticalAlignTopRounded"
import WrapTextRoundedIcon from "@mui/icons-material/WrapTextRounded"

import {
    Box,
    FormControlLabel,
    SelectChangeEvent,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material"
import { unionBy } from "lodash"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { lineHeight as lineHeightDefaultStyle } from "../../../../data/styles/simple/font.styles"
import { useCurrentCompanyStats } from "../../../../hooks/useCompanyStats"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { createAssetsFulfilled } from "../../../../store/slices/assets.slice"
import { setFontFamilyPreferencesAsync } from "../../../../store/slices/editor.slice"
import ExpandableSection from "../../../common/ExpandableSection"
import ButtonGroupInput from "../../../common/PropertyTools/ButtonGroupInput"
import ButtonSwitch from "../../../common/PropertyTools/ButtonSwitch"
import NumberInput from "../../../common/PropertyTools/NumberInput"
import PropertyRow from "../../../common/PropertyTools/PropertyRow"
import SelectDividerInput from "../../../common/PropertyTools/SelectDividerInput"
import SelectInput from "../../../common/PropertyTools/SelectInput"
import TextArea from "../../../common/PropertyTools/TextArea"
import DataSourceIndicator from "../../DataSource/DataSourceIndicator"
import SelectAttributes from "../../DataSource/SelectAttributes"
import { injectFontFaces } from "../../../../helpers/fontFace.helpers"
import { LoadFontButton } from "../LoadFontButton"

interface Props {
    object: TextI
    handleSetValue: (property: any, value: any) => void
    handleSetAttributeValue: (property: any, value: any) => void
    handleSetStyleValue: (property: any, value: any) => void
}

const TextAndFont = ({
    object,
    handleSetValue,
    handleSetAttributeValue,
    handleSetStyleValue,
}: Props) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const handleAppendAttribute = (attribute: string) => {
        handleSetValue(
            "text",
            !object.text || object.text === " " ? attribute : object.text + " " + attribute
        )
    }

    const preferencesFontFamily: string[] = useAppSelector(
        (state) => state.editor.value.settings.fontFamily
    )

    const { assets: companyAssets } = useCurrentCompanyStats()
    const assets = useAppSelector((state) => state.assets.data)

    const fontFamily: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontFamily"
    )
    const fontSize: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontSize"
    )
    const fontWeight: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontWeight"
    )
    const fontStyle: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "fontStyle"
    )
    const textAlign: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "textAlign"
    )
    const direction: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "direction"
    )
    const whiteSpace: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "whiteSpace"
    )
    const textTransform: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "textTransform"
    )
    const justifyContent: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "justifyContent"
    )
    let lineHeight: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "lineHeight"
    )
    if (!lineHeight) {
        lineHeight = lineHeightDefaultStyle
    }
    const letterSpacing: SimpleStyleT | undefined = object.styles.find(
        (style) => style.property === "letterSpacing"
    )

    const fonts = useMemo(() => {
        return unionBy(
            assets.filter((asset) => asset.type === "font"),
            companyAssets.filter((asset) => asset.type === "font"),
            "_id"
        )
    }, [assets, companyAssets])

    useEffect(() => {
        injectFontFaces(fonts)
    }, [fonts])

    if (
        !fontFamily &&
        !fontSize &&
        !fontWeight &&
        !fontStyle &&
        !textAlign &&
        !direction &&
        !whiteSpace &&
        !textTransform &&
        !justifyContent &&
        !lineHeight &&
        !letterSpacing
    )
        return <></>

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleSetValue(e.target.name, e.target.value || " ")

    const handleStyleOnChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        handleSetStyleValue(e.target.name, e.target.value)

    const handleFontFamilyOnChange = (e: SelectChangeEvent<any>) => {
        handleSetStyleValue(fontFamily?.property, e.target.value)
        if ((fontFamily?.input as SelectInputT).props.includes(e.target.value)) {
            dispatch(setFontFamilyPreferencesAsync(e.target.value))
        }
        const font = fonts.find((font) => font.name === e.target.value)
        if (font) {
            dispatch(createAssetsFulfilled([font]))
        }
    }

    const isTextEditable: boolean = object.attributes?.find(
        (attribute) => attribute.property === "isTextEditable"
    )?.value

    const textSizing: AttributeT | undefined = object.attributes?.find(
        (attribute) => attribute.property === "textSizing"
    )

    const handleChangeEditable = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        handleSetAttributeValue("isTextEditable", !isTextEditable)
    }

    return (
        <ExpandableSection
            expanded={true}
            title={"Text & font"}
            secondaryActionComponent={
                <Tooltip title={t("editor:textCanBeChanged")}>
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={isTextEditable}
                                name="isTextEditable"
                                onClick={handleChangeEditable}
                            />
                        }
                        label={<Typography variant="body2">{t("editor:editable")}</Typography>}
                    />
                </Tooltip>
            }
            component={
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                    width="100%"
                    pb={0.5}
                    // the theme gives every form control a 3px margin
                    sx={{ "& .MuiFormControl-root": { m: 0, width: "100%" } }}
                >
                    <PropertyRow
                        gutter={<SelectAttributes compact handleChange={handleAppendAttribute} />}
                    >
                        <TextArea
                            fullWidth
                            label={
                                <Box
                                    component="span"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                    }}
                                >
                                    {t("text")}
                                    <DataSourceIndicator content={object.text} fontSize="inherit" />
                                </Box>
                            }
                            name="text"
                            value={object.text}
                            onChange={handleOnChange}
                        />
                    </PropertyRow>
                    {fontFamily && (
                        <PropertyRow
                            gutter={
                                <LoadFontButton
                                    compact
                                    onChange={(value) =>
                                        handleSetStyleValue(fontFamily?.property, value)
                                    }
                                />
                            }
                        >
                            <SelectDividerInput
                                value={fontFamily.value}
                                label={fontFamily.input.label ?? fontFamily.property}
                                styleProperty={(fontFamily.input as SelectInputT).props}
                                stylePropertyBeforeDivider={[
                                    ...fonts.map((font) => font.name),
                                    ...preferencesFontFamily,
                                ]}
                                handleOnChange={handleFontFamilyOnChange}
                            />
                        </PropertyRow>
                    )}
                    {(fontSize || lineHeight) && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    {fontSize && (
                                        <NumberInput
                                            styleProperty={fontSize}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {lineHeight && (
                                        <NumberInput
                                            styleProperty={lineHeight}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}
                    {(letterSpacing || textTransform) && (
                        <PropertyRow>
                            <Box display="flex" gap={1}>
                                <Box flex={1} minWidth={0}>
                                    {letterSpacing && (
                                        <NumberInput
                                            styleProperty={letterSpacing}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    {textTransform && (
                                        <SelectInput
                                            styleProperty={textTransform}
                                            handleOnChange={handleStyleOnChange}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </PropertyRow>
                    )}
                    {/* horizontal and vertical text alignment, like the alignment row above */}
                    <PropertyRow>
                        <Box display="flex" gap={1} width="100%">
                            <Box flex={3} minWidth={0} display="flex">
                                {textAlign && (
                                    <ButtonGroupInput
                                        buttons={[
                                            {
                                                value: "left",
                                                tooltip: t("editor:textTools.textAlign.left"),
                                                icon: <FormatAlignLeftRoundedIcon />,
                                            },
                                            {
                                                value: "center",
                                                tooltip: t("editor:textTools.textAlign.center"),
                                                icon: <FormatAlignCenterRoundedIcon />,
                                            },
                                            {
                                                value: "right",
                                                tooltip: t("editor:textTools.textAlign.right"),
                                                icon: <FormatAlignRightRoundedIcon />,
                                            },
                                        ]}
                                        property={textAlign.property}
                                        value={textAlign.value}
                                        handleOnChange={handleSetStyleValue}
                                    />
                                )}
                            </Box>
                            <Box flex={3} minWidth={0} display="flex">
                                {justifyContent && (
                                    <ButtonGroupInput
                                        buttons={[
                                            {
                                                value: "flex-start",
                                                tooltip: t("editor:textTools.textAlign.top"),
                                                icon: <VerticalAlignTopRoundedIcon />,
                                            },
                                            {
                                                value: "center",
                                                tooltip: t("editor:textTools.textAlign.center"),
                                                icon: <VerticalAlignCenterRoundedIcon />,
                                            },
                                            {
                                                value: "flex-end",
                                                tooltip: t("editor:textTools.textAlign.bottom"),
                                                icon: <VerticalAlignBottomRoundedIcon />,
                                            },
                                        ]}
                                        property={justifyContent.property}
                                        value={justifyContent.value}
                                        handleOnChange={handleSetStyleValue}
                                    />
                                )}
                            </Box>
                        </Box>
                    </PropertyRow>
                    {/* three to a group, under the two alignment groups above */}
                    <PropertyRow>
                        <Box display="flex" gap={1} width="100%">
                            <Box flex={3} minWidth={0} display="flex" gap="3px">
                                <Box flex={1} minWidth={0} display="flex">
                                    {fontWeight && (
                                        <ButtonSwitch
                                            selectedButton={{
                                                value: "bold",
                                                tooltip: t("editor:textTools.fontWeight.bold"),
                                                icon: <FormatBoldRoundedIcon />,
                                            }}
                                            unselectedButton={{
                                                value: "normal",
                                                tooltip: t("editor:textTools.fontWeight.normal"),
                                                icon: <FormatBoldRoundedIcon />,
                                            }}
                                            property={fontWeight.property}
                                            value={fontWeight.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0} display="flex">
                                    {fontStyle && (
                                        <ButtonSwitch
                                            selectedButton={{
                                                value: "italic",
                                                tooltip: t("editor:textTools.fontStyle.italic"),
                                                icon: <FormatItalicRoundedIcon />,
                                            }}
                                            unselectedButton={{
                                                value: "normal",
                                                tooltip: t("editor:textTools.fontStyle.normal"),
                                                icon: <FormatItalicRoundedIcon />,
                                            }}
                                            property={fontStyle.property}
                                            value={fontStyle.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                                <Box flex={1} minWidth={0} display="flex">
                                    {textSizing && (
                                        <ButtonSwitch
                                            selectedButton={{
                                                value: "fitting",
                                                tooltip: t("editor:textTools.textSizing.fitting"),
                                                icon: <FormatShapesRoundedIcon />,
                                            }}
                                            unselectedButton={{
                                                value: "fixed",
                                                tooltip: t("editor:textTools.textSizing.fixed"),
                                                icon: <FormatShapesRoundedIcon />,
                                            }}
                                            property={textSizing.property}
                                            value={textSizing.value}
                                            handleOnChange={handleSetAttributeValue}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box flex={3} minWidth={0} display="flex" gap="3px">
                                <Box flex={1} minWidth={0} display="flex">
                                    {whiteSpace && (
                                        <ButtonSwitch
                                            selectedButton={{
                                                value: "normal",
                                                tooltip: t("editor:textTools.whiteSpace.normal"),
                                                icon: <WrapTextRoundedIcon />,
                                            }}
                                            unselectedButton={{
                                                value: "nowrap",
                                                tooltip: t("editor:textTools.whiteSpace.nowrap"),
                                                icon: <WrapTextRoundedIcon />,
                                            }}
                                            property={whiteSpace.property}
                                            value={whiteSpace.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                                <Box flex={2} minWidth={0} display="flex">
                                    {direction && (
                                        <ButtonGroupInput
                                            buttons={[
                                                {
                                                    value: "ltr",
                                                    tooltip: t(
                                                        "editor:textTools.textDirection.ltr"
                                                    ),
                                                    icon: <FormatTextdirectionLToRRoundedIcon />,
                                                },
                                                {
                                                    value: "rtl",
                                                    tooltip: t(
                                                        "editor:textTools.textDirection.rtl"
                                                    ),
                                                    icon: <FormatTextdirectionRToLRoundedIcon />,
                                                },
                                            ]}
                                            property={direction.property}
                                            value={direction.value}
                                            handleOnChange={handleSetStyleValue}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </PropertyRow>
                </Box>
            }
        />
    )
}

export default TextAndFont
